"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEVER_RELOAD = void 0;
const web3_1 = __importDefault(require("web3"));
function parseCallKey(callKey) {
    const pcs = callKey.split("-");
    if (pcs.length !== 2) {
        throw new Error(`Invalid call key: ${callKey}`);
    }
    return {
        address: pcs[0],
        callData: pcs[1]
    };
}
const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
const LOWER_HEX_REGEX = /^0x[a-f0-9]*$/;
function toCallKey(call) {
    if (!ADDRESS_REGEX.test(call.address)) {
        throw new Error(`Invalid address: ${call.address}`);
    }
    if (!LOWER_HEX_REGEX.test(call.callData)) {
        throw new Error(`Invalid hex: ${call.callData}`);
    }
    return `${call.address}-${call.callData}`;
}
function isMethodArg(x) {
    return ["string", "number"].indexOf(typeof x) !== -1;
}
function isValidMethodArgs(x) {
    return (x === undefined ||
        (Array.isArray(x) && x.every((xi) => isMethodArg(xi) || (Array.isArray(xi) && xi.every(isMethodArg)))));
}
const INVALID_RESULT = { valid: false, blockNumber: undefined, data: undefined };
// use this options object
exports.NEVER_RELOAD = {
    blocksPerFetch: Infinity
};
const INVALID_CALL_STATE = { valid: false, result: undefined, loading: false, syncing: false, error: false };
const LOADING_CALL_STATE = { valid: true, result: undefined, loading: true, syncing: true, error: false };
function toCallState(callResult, contractInterface, fragment, latestBlockNumber) {
    if (!callResult)
        return INVALID_CALL_STATE;
    const { valid, data, blockNumber } = callResult;
    if (!valid)
        return INVALID_CALL_STATE;
    if (valid && !blockNumber)
        return LOADING_CALL_STATE;
    if (!contractInterface || !fragment || !latestBlockNumber)
        return LOADING_CALL_STATE;
    const success = data && data.length > 2;
    const syncing = (blockNumber !== null && blockNumber !== void 0 ? blockNumber : 0) < latestBlockNumber;
    let result = undefined;
    if (success && data) {
        try {
            result = contractInterface.decodeFunctionResult(fragment, data);
        }
        catch (error) {
            console.debug("Result data parsing failed", fragment, data);
            return {
                valid: true,
                loading: false,
                error: true,
                syncing,
                result
            };
        }
    }
    return {
        valid: true,
        loading: false,
        syncing,
        result: result,
        error: !success
    };
}
class SwapMulticall {
    constructor(chainId, web3, swapContract) {
        this.chainId = chainId;
        this.web3 = new web3_1.default(web3.currentProvider);
        this.swapContract = swapContract;
    }
    async useBlockNumber() {
        const block = await this.web3.eth.getBlockNumber();
        return block - 10;
    }
    async fetchChunk(chunk, minBlockNumber) {
        let resultsBlockNumber, returnData;
        const multicallContract = this.swapContract.useMulticallContract();
        try {
            [resultsBlockNumber, returnData] = await (multicallContract === null || multicallContract === void 0 ? void 0 : multicallContract.aggregate(chunk.map((obj) => [obj.address, obj.callData])));
        }
        catch (error) {
            console.debug("Failed to fetch chunk inside retry", error);
            throw error;
        }
        if (resultsBlockNumber.toNumber() < minBlockNumber) {
            console.debug(`Fetched results for old block number: ${resultsBlockNumber.toString()} vs. ${minBlockNumber}`);
            throw new Error("Fetched for old block number");
        }
        return { results: returnData, blockNumber: resultsBlockNumber.toNumber() };
    }
    // the lowest level call for subscribing to contract data
    async useCallsData(calls) {
        var _a, _b;
        const chainId = this.chainId;
        // not need sort serialized keys
        const serializedCallKeys = JSON.stringify((_b = (_a = calls === null || calls === void 0 ? void 0 : calls.filter((c) => Boolean(c))) === null || _a === void 0 ? void 0 : _a.map(toCallKey)) !== null && _b !== void 0 ? _b : []);
        const callKeys = JSON.parse(serializedCallKeys);
        if (!chainId || callKeys.length === 0)
            return undefined;
        calls = callKeys.map((key) => parseCallKey(key));
        // @ts-ignore
        const res = await this.fetchChunk(calls, await this.useBlockNumber());
        const returnData = res.results;
        const callResults = {
            chainId,
            results: callKeys.reduce((memo, callKey, i) => {
                var _a;
                memo[callKey] = (_a = returnData[i]) !== null && _a !== void 0 ? _a : null;
                return memo;
            }, {}),
            blockNumber: res.blockNumber
        };
        return calls.map((call) => {
            if (!chainId || !call)
                return INVALID_RESULT;
            const results = callResults.results;
            const result = results[toCallKey(call)];
            let data;
            if (result && result !== "0x") {
                data = result;
            }
            return { valid: true, data, blockNumber: callResults === null || callResults === void 0 ? void 0 : callResults.blockNumber };
        });
    }
    async useSingleContractMultipleData(contract, methodName, callInputs) {
        var _a;
        const fragment = (_a = contract === null || contract === void 0 ? void 0 : contract.interface) === null || _a === void 0 ? void 0 : _a.getFunction(methodName);
        const calls = contract && fragment && callInputs && callInputs.length > 0
            ? callInputs.map((inputs) => {
                return {
                    address: contract.address,
                    callData: contract.interface.encodeFunctionData(fragment, inputs)
                };
            })
            : [];
        const results = await this.useCallsData(calls);
        const latestBlockNumber = await this.useBlockNumber();
        // @ts-ignore
        return results.map((result) => toCallState(result, contract === null || contract === void 0 ? void 0 : contract.interface, fragment, latestBlockNumber));
    }
    async useMultipleContractSingleData(addresses, contractInterface, methodName, callInputs) {
        const fragment = contractInterface.getFunction(methodName);
        const callData = fragment && isValidMethodArgs(callInputs)
            ? contractInterface.encodeFunctionData(fragment, callInputs)
            : undefined;
        const calls = fragment && addresses && addresses.length > 0 && callData
            ? addresses.map((address) => {
                return address && callData
                    ? {
                        address,
                        callData
                    }
                    : undefined;
            })
            : [];
        const results = await this.useCallsData(calls);
        const latestBlockNumber = await this.useBlockNumber();
        return results && results.map((result) => toCallState(result, contractInterface, fragment, latestBlockNumber));
    }
    async useSingleCallResult(contract, methodName, inputs) {
        var _a;
        const fragment = (_a = contract === null || contract === void 0 ? void 0 : contract.interface) === null || _a === void 0 ? void 0 : _a.getFunction(methodName);
        const calls = contract && fragment && isValidMethodArgs(inputs)
            ? [
                {
                    address: contract.address,
                    callData: contract.interface.encodeFunctionData(fragment, inputs)
                }
            ]
            : [];
        const callResults = await this.useCallsData(calls);
        const latestBlockNumber = await this.useBlockNumber();
        // @ts-ignore
        return toCallState(callResults[0], contract === null || contract === void 0 ? void 0 : contract.interface, fragment, latestBlockNumber);
    }
}
exports.default = SwapMulticall;
